import { useRef, useState, useMemo } from "react";
import { Upload, ImageIcon, FileText, Trash2 } from "lucide-react";
import toast from "react-hot-toast";

/**
 * FileDropZone
 *
 * Props:
 * - file: string | null               // current file (e.g., data URL)
 * - setFile: (dataUrl: string|null)   // setter for file preview (data URL)
 * - allowedTypes?: string[]           // e.g. ["image/jpeg","image/png","application/pdf",".doc",".docx","image/*"]
 * - maxSizeMB?: number                // default 15
 * - onFileMetaChange?: (meta) => void // optional: receive {name,size,type}
 * - labelText?: string                // override drop label
 * - hintText?: string                 // override hint
 */
const FileDropZone = ({
  file,
  setFile,
  allowedTypes = [
    "image/jpeg",
    "image/png",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
  maxSizeMB = 15,
  onFileMetaChange,
  labelText = "Drop your file here",
  hintText = "or click to browse",
}) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileMeta, setFileMeta] = useState({ name: "", size: 0, type: "" });

  // --- Helpers -----------------------------------------------------

  // Normalize entries: ".png" or "png" -> extension; "image/png" or "image/*" -> MIME
  const normalizedRules = useMemo(() => {
    return (allowedTypes || []).map((ruleRaw) => {
      const rule = String(ruleRaw).trim().toLowerCase();
      if (!rule) return null;

      if (rule.includes("/")) {
        // MIME or wildcard MIME
        return { kind: "mime", value: rule }; // e.g., "image/png", "image/*"
      }

      // Extension path: ensure leading dot
      const ext = rule.startsWith(".") ? rule : `.${rule}`;
      return { kind: "ext", value: ext };
    }).filter(Boolean);
  }, [allowedTypes]);

  // Build input accept= attribute from rules
  const acceptAttr = useMemo(() => {
    // For MIME rules: keep as is (e.g., 'image/*', 'application/pdf')
    // For ext rules: keep with dot (e.g., '.png')
    return normalizedRules
      .map((r) => (r.kind === "mime" ? r.value : r.value))
      .join(",");
  }, [normalizedRules]);

  const formatSizeMB = (bytes) => (bytes / 1024 / 1024).toFixed(2);

  const getExtension = (filename = "") => {
    const idx = filename.lastIndexOf(".");
    if (idx === -1) return "";
    return filename.slice(idx).toLowerCase(); // includes dot
  };

  const isTypeAllowed = (fileObj) => {
    // If no rules provided, allow all
    if (!normalizedRules.length) return true;

    const mime = (fileObj.type || "").toLowerCase();
    const ext = getExtension(fileObj.name || "");

    // Check MIME rules first (supports wildcards like image/*)
    const mimeOk = normalizedRules.some((r) => {
      if (r.kind !== "mime") return false;
      if (r.value.endsWith("/*")) {
        const base = r.value.split("/")[0];
        return mime.startsWith(`${base}/`);
      }
      return mime === r.value;
    });

    // If explicit mime matched, accept
    if (mimeOk) return true;

    // Otherwise, fall back to extension rules
    const extOk = normalizedRules.some((r) => r.kind === "ext" && r.value === ext);
    return extOk;
  };

  // --- Events ------------------------------------------------------

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files?.[0];
    processFile(droppedFile);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    processFile(selectedFile);
  };

  const processFile = (fileInput) => {
    if (!fileInput) return;

    // Size validation
    const limitBytes = maxSizeMB * 1024 * 1024;
    if (fileInput.size > limitBytes) {
      toast.error(`File size exceeds ${maxSizeMB} MB limit`);
      return;
    }

    // Type/extension validation
    if (!isTypeAllowed(fileInput)) {
      const readable = allowedTypes.length
        ? allowedTypes.join(", ")
        : "any";
      toast.warning(`This file type is not allowed. Allowed: ${readable}`);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(fileInput);
    reader.onload = () => {
      setFile(reader.result);
      const meta = {
        name: fileInput.name,
        size: fileInput.size,
        type: fileInput.type,
      };
      setFileMeta(meta);
      onFileMetaChange?.(meta);
    };
  };

  const removeFile = () => {
    setFile(null);
    setFileMeta({ name: "", size: 0, type: "" });
    onFileMetaChange?.(null);
  };

  const isImage = (fileMeta.type || "").startsWith("image/");
  const friendlyLabel = (type) => {
    const map = {
      "application/pdf": "PDF",
      "image/jpeg": "JPEG",
      "image/png": "PNG",
      "image/webp": "WEBP",
      "image/gif": "GIF",
      "application/msword": "Word (DOC)",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word (DOCX)",
      ".doc": "Word (DOC)",
      ".docx": "Word (DOCX)",
      ".pdf": "PDF",
      ".jpg": "JPEG",
      ".jpeg": "JPEG",
      ".png": "PNG",
    };
    const key = type.toLowerCase();
    return map[key] || type;
  };

  // --- UI ----------------------------------------------------------

  return (
    <div className="mt-3">
      {!file ? (
        <div
          className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all ${isDragging
            ? "border-blue-500 bg-blue-500/10"
            : "border-neutral-300 dark:border-neutral-700 hover:border-blue-500/50 hover:bg-blue-500/5"
            }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept={acceptAttr || undefined}
            className="hidden"
          />
          <div className="flex flex-col items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
              <Upload size={16} className="text-blue-500" />
            </div>
            <div className="text-neutral-700 dark:text-neutral-300 text-xs font-medium">
              {labelText}
            </div>
            <p className="text-neutral-500 text-xs">
              {hintText}
              {allowedTypes?.length ? (
                <>
                  {" "}
                  {allowedTypes.length === 1 ? (
                    <span>

                      <span className="font-medium">
                        (Only{" "} {friendlyLabel(allowedTypes[0])} format allowed)
                      </span>{" "}

                    </span>
                  ) : (
                    <>
                      (<span className="font-medium">Allowed:</span>{" "}
                      {allowedTypes.map((t) => friendlyLabel(t)).join(", ")})
                    </>
                  )}
                </>
              ) : null}

            </p>
            <p className="text-neutral-400 text-[11px]">Max size: {maxSizeMB} MB</p>
          </div>
        </div>
      ) : (
        <div className="relative border rounded-xl overflow-hidden border-neutral-300 dark:border-neutral-700">
          <div className="absolute top-2 right-2 z-10 flex gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-1.5 bg-neutral-800/80 backdrop-blur-sm rounded-full hover:bg-neutral-700 transition-colors"
              aria-label="Change file"
            >
              {isImage ? (
                <ImageIcon size={12} className="text-white" />
              ) : (
                <FileText size={12} className="text-white" />
              )}
            </button>
            <button
              onClick={removeFile}
              className="p-1.5 bg-red-500/80 backdrop-blur-sm rounded-full hover:bg-red-600 transition-colors"
              aria-label="Remove file"
            >
              <Trash2 size={12} className="text-white" />
            </button>
          </div>

          {isImage ? (
            <img src={file} alt="Uploaded" className="w-full h-32 object-cover" />
          ) : (
            <div className="flex items-center justify-center h-32 bg-neutral-100 dark:bg-neutral-800">
              <FileText size={40} className="text-neutral-500" />
            </div>
          )}

          <div className="p-2 bg-neutral-100 dark:bg-neutral-800">
            <div className="text-neutral-700 dark:text-neutral-300 text-xs font-medium truncate">
            </div>
            <div className="text-neutral-500 text-xs">
              {formatSizeMB(fileMeta.size)} MB
            </div>
          </div>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept={acceptAttr || undefined}
            className="hidden"
          />
        </div>
      )}
    </div>
  );
};

export default FileDropZone;
